import { useEffect } from 'react'

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-8">
      <h2 className="font-display font-black text-lg tracking-widest text-bear uppercase mb-3">
        {title}
      </h2>
      <div className="space-y-3 text-sm leading-relaxed text-stone-700">{children}</div>
    </section>
  )
}

export default function LegalPage() {
  useEffect(() => {
    document.title = 'Kodiak Labs — Privacy, Terms & SMS Policy'
    // Native anchor scroll races the React mount on full-page loads; re-scroll once mounted.
    const id = window.location.hash.slice(1)
    if (id) document.getElementById(id)?.scrollIntoView()
  }, [])

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-12">
      <div className="max-w-2xl mx-auto">
        <div className="flex flex-col items-center mb-10">
          <img src="/bear_icon_transparent.svg" alt="" className="h-16 w-auto mb-4" />
          <h1 className="font-display font-black text-2xl tracking-widest text-bear uppercase leading-none">
            Kodiak Labs
          </h1>
          <p className="font-mono text-[10px] tracking-[0.25em] text-stone-400 uppercase mt-1">
            Privacy · Terms · SMS Policy
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 p-8 space-y-10">
          <Section id="about" title="Who we are">
            <p>
              Kodiak Labs is the consumer brand of Net Don't Lie Enterprises,
              Inc., which owns and operates kodiaklabs.com and
              app.kodiaklabs.com. Kodiak Labs builds a smart fishing net for
              sport anglers. Kodiak is currently in a private, invite-only
              beta.
            </p>
            <p>
              Questions about anything on this page? Contact us at{' '}
              <a href="mailto:info@kodiaklabs.com" className="underline hover:text-stone-900">
                info@kodiaklabs.com
              </a>.
            </p>
          </Section>

          <Section id="sms-program" title="SMS program">
            <p>
              Kodiak Labs uses SMS to keep users informed about their account and their net,
              and to provide support. If you opt in, you may receive the following types of
              messages:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Account notifications</strong> — sign-in verification codes and
                account or security updates.
              </li>
              <li>
                <strong>Event alerts</strong> — notifications about your net and catch data,
                such as new catch data ready for review or your net being offline.
              </li>
              <li>
                <strong>Customer support</strong> — two-way messaging with the Kodiak Labs
                team, including troubleshooting and logging a caught fish via SMS.
              </li>
            </ul>
            <p>
              <strong>How opt-in works.</strong> Kodiak is currently invite-only. During
              onboarding, invited users may add a phone number, check an unchecked consent
              box agreeing to receive the message types above, and verify the number with a
              one-time code. Providing a phone number is optional and is not a condition of
              using the service.
            </p>
            <p>
              <strong>Opting out.</strong> Reply <strong>STOP</strong> to any message to
              cancel at any time. Reply <strong>HELP</strong> for help, or email{' '}
              <a href="mailto:info@kodiaklabs.com" className="underline hover:text-stone-900">
                info@kodiaklabs.com
              </a>.
            </p>
            <p>
              Message frequency varies. Message and data rates may apply. Carriers are not
              liable for delayed or undelivered messages.
            </p>
            <p>
              <strong>
                We do not share or sell your mobile number or SMS consent to third parties or
                affiliates for marketing purposes.
              </strong>
            </p>
          </Section>

          <Section id="privacy" title="Privacy policy">
            <p>
              <strong>What we collect.</strong> Account information (name, email address, and
              phone number if you choose to provide one), catch data recorded by your net
              (weight, length, GPS location, and images), and device telemetry used to keep
              your net working.
            </p>
            <p>
              <strong>How we use it.</strong> To operate the service: storing and displaying
              your catch data, sending the notifications described in the SMS program above,
              securing your account, and providing support. We do not sell your personal
              information, and we do not share your mobile number or SMS consent with third
              parties or affiliates for marketing purposes.
            </p>
            <p>
              <strong>Storage and security.</strong> Data is stored on Amazon Web Services
              infrastructure in the United States and encrypted in transit.
            </p>
            <p>
              <strong>Your choices.</strong> You can opt out of SMS at any time by replying
              STOP. To access, correct, or delete your data, email{' '}
              <a href="mailto:info@kodiaklabs.com" className="underline hover:text-stone-900">
                info@kodiaklabs.com
              </a>{' '}
              and we'll handle it promptly.
            </p>
          </Section>

          <Section id="terms" title="Terms of service">
            <p>
              Kodiak is an invite-only beta service provided by Net Don't Lie
              Enterprises, Inc., doing business as Kodiak Labs. By using the app
              you agree to these terms.
            </p>
            <p>
              <strong>Acceptable use.</strong> Use the service only with a Kodiak net and
              account you're authorized to access. Don't attempt to disrupt the service or
              access other users' data.
            </p>
            <p>
              <strong>Your data.</strong> You retain rights to your catch data. You grant
              Kodiak Labs permission to store and process it in order to provide the service.
            </p>
            <p>
              <strong>No warranty.</strong> The service is provided "as is" during beta,
              without warranties of any kind. We may modify or discontinue features, and we
              may suspend accounts that misuse the service. Our liability is limited to the
              maximum extent permitted by law.
            </p>
            <p>
              We may update these terms as the service evolves; material changes will be
              communicated to active users. Questions:{' '}
              <a href="mailto:info@kodiaklabs.com" className="underline hover:text-stone-900">
                info@kodiaklabs.com
              </a>.
            </p>
          </Section>

          <Section id="invite-only" title="Invite-only access">
            <p>
              Kodiak is currently invite-only. Sign-ups are limited to invited users, and
              invited users opt in to SMS notifications during onboarding as described in the
              SMS program section above.
            </p>
          </Section>

          <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-400 pt-2">
            Last updated: August 20, 2026
            <br />© 2026 Net Don't Lie Enterprises, Inc. d/b/a Kodiak Labs
          </p>
        </div>

        <p className="text-center mt-6 font-mono text-[10px] tracking-[0.15em] uppercase">
          <a href="/" className="text-stone-400 hover:text-stone-700 transition-colors">
            Back to sign in
          </a>
        </p>
      </div>
    </div>
  )
}
